// Destination de retour post-déconnexion.
//
// Une application appelante (Apply, le portail étudiant…) envoie l'utilisateur ici
// pour le déconnecter, puis attend d'être reprise. La destination ne peut pas venir
// telle quelle de la query string ou du référent : seule l'API sait si son origine
// correspond à un client OAuth enregistré — c'est elle qui, dans sa réponse à
// `/authentication/v1.0/logout`, renvoie `redirect_url` une fois le candidat validé.

import { RouteLocationNormalized } from 'vue-router'
import { isNonEmptyString, readSingleQueryParameterValue } from '@/services/RedirectTargetValidation'

/**
 * Clé de stockage dédiée, distincte de `authentication_redirect_context` : la
 * déconnexion voulue purge cette dernière (cf. `Actions.LOGOUT_USER`), et la
 * destination de retour post-déconnexion doit au contraire lui survivre jusqu'à sa
 * consommation par `/logout` ou `/logout/callback`.
 */
const STORAGE_KEY = 'post_logout_redirect_target'

/**
 * Préfixe des messages de journal, pour les retrouver dans une console.
 */
const LOG_PREFIX = '[PostLogoutRedirect]'

/**
 * Nom du paramètre de query string par lequel une application appelante indique où
 * revenir après la déconnexion.
 */
const POST_LOGOUT_REDIRECT_QUERY_PARAMETER_NAME = 'post_logout_redirect_uri'

/**
 * Détermine l'origine candidate vers laquelle revenir après la déconnexion, sans la
 * valider : le paramètre de requête explicite d'abord ; à défaut, le référent,
 * seulement s'il pointe ailleurs que cette application — un référent interne (l'écran
 * de connexion lui-même, par exemple) n'est jamais une destination de retour.
 *
 * La validation elle-même n'a plus lieu ici : le candidat part tel quel en paramètre
 * `post_logout_redirect_uri` de `GET /authentication/v1.0/logout`, et c'est la réponse
 * de cet appel — via `Actions.LOGOUT_USER` — qui porte le `redirect_url` validé.
 */
export function readCandidatePostLogoutRedirectUri (route: RouteLocationNormalized): string | null {
  const queryCandidate = readSingleQueryParameterValue(
    route.query[POST_LOGOUT_REDIRECT_QUERY_PARAMETER_NAME]
  )
  if (queryCandidate !== null) {
    return queryCandidate
  }

  const referrer = document.referrer
  if (!isNonEmptyString(referrer)) {
    return null
  }

  try {
    const referrerOrigin = new URL(referrer).origin
    return referrerOrigin !== window.location.origin ? referrer : null
  } catch (error) {
    return null
  }
}

/**
 * Mémorise la destination de retour validée par l'API, à appeler avec le
 * `redirect_url` renvoyé par `GET /authentication/v1.0/logout`.
 */
export function savePostLogoutRedirectTarget (target: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, target)
  } catch (error) {
    console.warn(`${LOG_PREFIX} écriture impossible`, error)
  }
}

/**
 * Lit puis efface la destination de retour post-déconnexion. À usage unique : ni
 * `/logout` ni `/logout/callback` ne doivent la faire resservir à une déconnexion
 * ultérieure.
 */
export function consumePostLogoutRedirectTarget (): string | null {
  try {
    const target = sessionStorage.getItem(STORAGE_KEY)
    if (target !== null) {
      sessionStorage.removeItem(STORAGE_KEY)
    }
    return target
  } catch (error) {
    console.warn(`${LOG_PREFIX} lecture impossible`, error)
    return null
  }
}
